import axios from 'axios';
import * as React from 'react';

import useCountriesHomePage, {
  defaultCountry,
} from '../hooks/use-countries-homepage';

const UserIpAddressContext = React.createContext();

/**
 * ---------------------------------------
 * Constants
 * ---------------------------------------
 */

const DEFAULT_COUNTRY_ID = 9;
const CACHE_TTL_MINUTES = 60;
const API_TIMEOUT = 3000;

/**
 * ---------------------------------------
 * Cache Helpers
 * ---------------------------------------
 */

const setCache = (
  key,
  value,
  ttlMinutes = CACHE_TTL_MINUTES
) => {
  try {
    const data = {
      value,
      expiry:
        Date.now() +
        ttlMinutes * 60 * 1000,
    };

    sessionStorage.setItem(
      key,
      JSON.stringify(data)
    );
  } catch (e) {
    console.error(
      `Cache save error: ${key}`,
      e
    );
  }
};

const getCache = (
  key,
  defaultValue = null
) => {
  try {
    const item =
      sessionStorage.getItem(key);

    if (!item) {
      return defaultValue;
    }

    const parsed =
      JSON.parse(item);

    /**
     * Cache expired
     */

    if (
      !parsed?.expiry ||
      Date.now() > parsed.expiry
    ) {
      sessionStorage.removeItem(
        key
      );

      return defaultValue;
    }

    return parsed.value;
  } catch {
    return defaultValue;
  }
};

/**
 * ---------------------------------------
 * GEO APIs
 * ---------------------------------------
 */

const GEO_APIS = [
  {
    url: 'https://ipapi.co/json/',
    parse: (data) => ({
      ip: data.ip,
      country: data.country_code,
    }),
  },
  {
    url: 'https://ipinfo.io/json',
    parse: (data) => ({
      ip: data.ip,
      country: data.country,
    }),
  },
  {
    url: 'https://api.db-ip.com/v2/free/self',
    parse: (data) => ({
      ip: data.ipAddress,
      country: data.countryCode,
    }),
  },
  {
    url: 'https://api.ipbase.com/v1/json/',
    parse: (data) => ({
      ip: data.ip,
      country: data.country_code,
    }),
  },
];

/**
 * ---------------------------------------
 * Provider
 * ---------------------------------------
 */

function UserIpAddressProvider(props) {
  const { data } =
    useCountriesHomePage();

  /**
   * ---------------------------------------
   * Cached Values
   * ---------------------------------------
   */

  const cachedCountries =
    React.useMemo(() => {
      return getCache(
        'cached_countries',
        null
      );
    }, []);

  const cachedLocationId =
    React.useMemo(() => {
      return getCache(
        'cached_location_id',
        DEFAULT_COUNTRY_ID
      );
    }, []);

  const cachedIp =
    React.useMemo(() => {
      return getCache(
        'cached_ip_address',
        ''
      );
    }, []);

  const cachedCountryCode =
    React.useMemo(() => {
      return getCache(
        'cached_detected_country_code',
        ''
      );
    }, []);

  /**
   * ---------------------------------------
   * States
   * ---------------------------------------
   */

  const [locationId, setLocationId] =
    React.useState(
      cachedLocationId
    );

  const [ipAddress, setIpAddress] =
    React.useState(cachedIp);

  const [countries, setCountries] =
    React.useState(
      cachedCountries || [
        defaultCountry,
      ]
    );

  /**
   * ---------------------------------------
   * Get Location ID
   * ---------------------------------------
   */

  const getLocationId =
    React.useCallback(
      (
        location,
        shouldSaveCountry = true
      ) => {
        if (!location) {
          setLocationId(
            DEFAULT_COUNTRY_ID
          );

          return;
        }

        const matchedCountry =
          countries.find(
            (country) =>
              location ===
              country.ISO2 ||
              location ===
              country.CountryDescription
          );

        const nextLocationId =
          matchedCountry
            ? matchedCountry.CountryId
            : DEFAULT_COUNTRY_ID;

        setLocationId(
          nextLocationId
        );

        /**
         * Save cache
         */

        setCache(
          'cached_location_id',
          nextLocationId
        );

        if (shouldSaveCountry) {
          setCache(
            'cached_detected_country_code',
            location
          );
        }
      },
      [countries]
    );

  /**
   * ---------------------------------------
   * Fetch Geo Details
   * ---------------------------------------
   */

  const getLocationDetails =
    React.useCallback(
      async () => {
        /**
         * Use cached country first
         */

        if (
          cachedIp &&
          cachedCountryCode
        ) {
          setIpAddress(cachedIp);

          getLocationId(
            cachedCountryCode,
            false
          );

          return;
        }

        /**
         * Sequential fallback APIs
         */

        for (const api of GEO_APIS) {
          try {
            const response =
              await axios.get(
                api.url,
                {
                  timeout:
                    API_TIMEOUT,
                }
              );

            if (
              !response?.data
            ) {
              continue;
            }

            const parsed =
              api.parse(
                response.data
              );

            /**
             * Validate response
             */

            if (
              parsed?.ip &&
              parsed?.country
            ) {
              /**
               * Update states
               */

              setIpAddress(
                parsed.ip
              );

              getLocationId(
                parsed.country
              );

              /**
               * Save cache
               */

              setCache(
                'cached_ip_address',
                parsed.ip
              );

              setCache(
                'cached_detected_country_code',
                parsed.country
              );

              return;
            }
          } catch (error) {
            console.error(
              `Geo API failed: ${api.url}`,
              error?.message
            );
          }
        }

        console.error(
          'All GEO APIs failed.'
        );
      },
      [
        cachedIp,
        cachedCountryCode,
        getLocationId,
      ]
    );

  /**
   * ---------------------------------------
   * Fetch Countries
   * ---------------------------------------
   */

  React.useEffect(() => {
    const countryList =
      data?.CountryDetails
        ?.CountryDetails;

    if (countryList?.length) {
      setCountries(countryList);

      /**
       * Save countries cache
       */

      setCache(
        'cached_countries',
        countryList,
        60 * 24
      );
    }
  }, [data]);

  /**
   * ---------------------------------------
   * Fetch Location Once
   * ---------------------------------------
   */

  React.useEffect(() => {
    if (countries?.length) {
      getLocationDetails();
    }
  }, [
    countries,
    getLocationDetails,
  ]);

  /**
   * ---------------------------------------
   * Context Value
   * ---------------------------------------
   */

  const value = React.useMemo(
    () => ({
      countries,
      locationId,
      getLocationId,
      ipAddress,
    }),
    [
      countries,
      locationId,
      getLocationId,
      ipAddress,
    ]
  );

  return (
    <UserIpAddressContext.Provider
      value={value}
      {...props}
    />
  );
}

export {
  UserIpAddressProvider as default,
  UserIpAddressContext,
};